<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import { getBoardIdsOfGroupedStickies } from "@/backend/read/BackendReader";
import { cardKey } from "@/components/StickyNote/injectKeys";
import MirrorSticky from "@/components/menu/MirrorSticky/MirrorSticky.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { injectStrict } from "@/utils/vue";

import AttributeChip from "./AttributeChip.vue";

const card = injectStrict(cardKey);

// TODO: this will need to be cached
const boardsStickyMirroredTo = ref<string[] | undefined>();

const refetchMirroredBoards = async () => {
  const { boardIds } = await getBoardIdsOfGroupedStickies(card.id);
  boardsStickyMirroredTo.value = boardIds.filter((id) => id !== useBoardStore().currentBoard().id);
};

onMounted(() => refetchMirroredBoards());

watch(
  () => Object.keys(useCardStore().cards),
  (newKeys, oldKeys) => {
    const symmetricDifference = (keysA: string[], keysB: string[]): string[] => [
      ...keysA.filter((x) => !keysB.includes(x)),
      ...keysB.filter((x) => !keysA.includes(x)),
    ];

    if (newKeys.length < oldKeys.length) return;

    const diff = symmetricDifference(newKeys, oldKeys);
    const cardCopyId = diff.find((cardId: string) => {
      const groupId = useCardStore().cards[cardId]?.groupId;
      return groupId && [card.id, card.groupId].includes(groupId);
    });
    if (cardCopyId) {
      void refetchMirroredBoards();
    }
  },
);

const mirrorCount = computed(() => {
  const count = boardsStickyMirroredTo.value?.length || 0;
  return count === 0 ? "" : count.toString();
});

const boards = computed(() => useBoardsStore().mirrorTargetBoards([card]));
</script>

<template>
  <template v-if="boardsStickyMirroredTo && boards.length !== 0">
    <slot name="prefix" />
    <AttributeChip
      name="mirror-chip"
      text-placeholder="0"
      :tooltip="$t('cardAction.mirror')"
      :text="mirrorCount"
      :dropdown-props="{ width: 'lg' }"
    >
      <template #icon>
        <SvgIcon name="general/mirror" />
      </template>
      <MirrorSticky @mirror="refetchMirroredBoards" />
    </AttributeChip>
  </template>
</template>
